import 'src/modules/locales/utils/i18nInit';
import { useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Router } from './router';
import { FullScreenProvider } from './hooks/useFullScreen';
import 'react-tooltip/dist/react-tooltip.css';
import { FreeSpinsContext, useIniFreeSpins } from './hooks/useFreeSpins';
import { API_URL, CONTENT_URL, isDev } from './api';
import {
  ApiProvider,
  HandleOpenErrorMessage,
  MESSAGE_STATUS,
  ModalContextProvider
} from 'react-easyrocket';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { TooltipProvider } from './context/useProgressiveTooltip';
import { SettingsProvider } from './modules/settings/provider';
import { DrawersProvider } from './modules/drawers';
import { MODAL_MAP } from './modals/config';
import { QueryBoundaries } from './components/QueryBoundaries';
import { VideoPreloader } from './components/VideoPreloader/VideoPreloader';
import { useScripts } from './hooks/useScripts';
import { SPORT_URL } from './modules/sport/config';
import { MessagesProvider } from './modules/messages/provider';
import { useInitMessagesContext } from './modules/messages/hooks/useInitMessagesContext';

export const App = () => {
  const { oldFreeSpins, saveFreeSpins, freeSpinsChanged, setFreeSpinsChanged } = useIniFreeSpins();
  const messageContextValue = useInitMessagesContext();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: false
          }
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // @ts-ignore
  const handleOpenErrorMessage: HandleOpenErrorMessage = (text, errorData) => {
    if (errorData?.code === 27) return;

    messageContextValue.openMessage({
      type: 'USER_MESSAGE',
      props: { text, status: MESSAGE_STATUS.ERROR, errorData },
      saveToHistory: false,
      preventDuplicates: true,
      id: text
    });
  };

  useScripts([SPORT_URL]);

  return (
    <FullScreenProvider>
      <DrawersProvider config={['top', 'left', 'topbarSearch', 'providersTab']}>
        <MessagesProvider contextValue={messageContextValue}>
          <FreeSpinsContext.Provider
            value={{ oldFreeSpins, saveFreeSpins, freeSpinsChanged, setFreeSpinsChanged }}>
            <QueryClientProvider client={queryClient}>
              {isDev && <ReactQueryDevtools initialIsOpen={false} />}
              <QueryBoundaries>
                <ApiProvider
                  openErrorMessage={handleOpenErrorMessage}
                  apiUrl={API_URL}
                  fallbackComponent={<VideoPreloader />}
                  contentUrl={CONTENT_URL}>
                  <TooltipProvider>
                    <ModalContextProvider modalMap={MODAL_MAP}>
                      <SettingsProvider>
                        <Router />
                      </SettingsProvider>
                    </ModalContextProvider>
                  </TooltipProvider>
                </ApiProvider>
              </QueryBoundaries>
            </QueryClientProvider>
          </FreeSpinsContext.Provider>
        </MessagesProvider>
      </DrawersProvider>
    </FullScreenProvider>
  );
};
